import { useEffect, useMemo, useRef, useState } from 'react';
import { isAlgoliaConfigured, searchCalibres } from '../services/algolia';
import {
  mergeCalibreSearchResults,
  type CalibreSearchable,
} from '../services/calibreSearchQuery';

/**
 * Long enough that a normal typing burst is one request, short enough that the
 * list feels live. Matches `useBrandSearch` and the reference listing.
 */
const DEFAULT_DEBOUNCE_MS = 250;

/**
 * The calibres index holds ~1,822 records, so one page covers any query — the
 * caller filters, sorts and paginates the result itself. Only `objectID` is
 * retrieved (the caller already holds every row), which keeps a keystroke's
 * response to a few KB no matter how broad the query.
 */
const DEFAULT_HITS_PER_PAGE = 1000;
const ID_ONLY = ['objectID'] as const;

/**
 * Filter a locally-held calibre list by a search term using Algolia, falling
 * back to the caller's own substring match when Algolia can't answer.
 *
 * The sibling of `useBrandSearch`, and the counterpart to `useCalibreSearch`:
 * that one HYDRATES hit ids into Firestore documents for the pickers, which pay
 * ~50 document reads per search because they hold nothing. This one is for the
 * surface that already holds every calibre — the listing — so Algolia decides
 * only WHICH rows match and in what order, and costs zero Firestore reads.
 *
 * What the index buys over the substring filter the listing used to run: typo
 * tolerance, prefix matching, accent folding, and — since watchlock #864 —
 * matching on the denormalised `manufacturerName`, `brandNames` and `familyName`,
 * none of which are on a calibre row at all. Typing "sellita" now finds every
 * calibre that maker built, not just the ones with "Sellita" in their own name.
 *
 * `items` should be referentially stable (state or a `useMemo`) — it is a
 * dependency of the returned memo, so a fresh array every render re-derives the
 * result every render.
 *
 * `matches` is the offline fallback predicate. It is the caller's, not this
 * hook's, because the listing's box has always matched its brand column too and
 * a degraded search must never match LESS than it did before Algolia.
 */
export function useCalibreListSearch<T extends CalibreSearchable>(
  items: T[],
  term: string,
  opts?: {
    debounceMs?: number;
    hitsPerPage?: number;
    matches?: (item: T, term: string) => boolean;
  },
): { results: T[]; searching: boolean; activeTerm: string } {
  const debounceMs = opts?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const hitsPerPage = opts?.hitsPerPage ?? DEFAULT_HITS_PER_PAGE;
  const matches = opts?.matches;

  const [debouncedTerm, setDebouncedTerm] = useState(term);
  /** Ranked ids from Algolia, or null for "no Algolia answer" (see below). */
  const [hitIds, setHitIds] = useState<string[] | null>(null);
  const [searching, setSearching] = useState(false);
  /**
   * Guards against out-of-order responses: a slow request for "sw2" must not
   * overwrite the already-arrived result for "sw200-1".
   */
  const requestSeq = useRef(0);

  useEffect(() => {
    // Clearing the box restores the full list immediately — waiting out the
    // debounce to show MORE rows just looks like lag.
    if (!term.trim()) {
      setDebouncedTerm(term);
      return;
    }
    const timer = setTimeout(() => setDebouncedTerm(term), debounceMs);
    return () => clearTimeout(timer);
  }, [term, debounceMs]);

  useEffect(() => {
    const trimmed = debouncedTerm.trim();
    if (!trimmed || !isAlgoliaConfigured()) {
      setHitIds(null);
      setSearching(false);
      return;
    }
    const seq = ++requestSeq.current;
    setSearching(true);
    // No filters: the listing's own filters (brand, generic type, deprecated)
    // are applied locally over the full set it holds, and pushing them here as
    // facet filters would make each one cost a round trip for no gain.
    searchCalibres(trimmed, {}, 0, hitsPerPage, { attributes: ID_ONLY })
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setHitIds(res.hits.map((h) => h.objectID));
      })
      .catch((error) => {
        if (seq !== requestSeq.current) return;
        // Non-fatal: null drops the merge back to the local substring filter,
        // which is exactly what this listing did before Algolia.
        console.warn('Calibre search unavailable, using local filter', error);
        setHitIds(null);
      })
      .finally(() => {
        if (seq === requestSeq.current) setSearching(false);
      });
  }, [debouncedTerm, hitsPerPage]);

  const results = useMemo(
    () => mergeCalibreSearchResults(items, hitIds, debouncedTerm, matches),
    [items, hitIds, debouncedTerm, matches],
  );

  // `activeTerm` is the term `results` is actually FOR — the debounced one, not
  // the box. The relevance sort keys off it so the listing doesn't flip into
  // ranked order a beat before the ranked ids arrive, which would show one
  // order and then jump to another.
  return { results, searching, activeTerm: debouncedTerm };
}
